import React from "react"
import clsx from "clsx"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { inputVariants } from "@react/shared/ui/Input"
import Label from "@react/shared/ui/Label"
import Segmented from "./Segmented"

export const fieldClass = inputVariants({ size: "sm" })

export const initialFields = (settings, assignment = {}) => ({
  projectId: assignment.project_id || "",
  taskId:    assignment.task_id || "",
  mode:      assignment.starts_at && assignment.ends_at ? "times" : "hours",
  hours:     assignment.planned_hours != null ? String(assignment.planned_hours) : "",
  startsAt:  assignment.starts_at || settings.day_start || "08:00",
  endsAt:    assignment.ends_at || settings.day_end || "17:00",
  note:      assignment.note || "",
})

export const fieldsToAttrs = fields => ({
  project_task_id: fields.taskId || "",
  planned_hours:   fields.mode === "hours" ? fields.hours : "",
  starts_at:       fields.mode === "times" ? fields.startsAt : "",
  ends_at:         fields.mode === "times" ? fields.endsAt : "",
  note:            fields.note,
})

export const projectLabel = project => [project.code, project.name].filter(Boolean).join(" · ")

const Field = ({ label, children, className }) => (
  <div className={clsx("flex flex-col gap-1", className)}>
    <Label size="xs" tone="default">{label}</Label>
    {children}
  </div>
)

const AssignmentFields = ({ fields, onChange, projects, settings, showProject = true, autoFocus }) => {
  const set     = patch => onChange({ ...fields, ...patch })
  const project = projects.find(item => item.id === Number(fields.projectId))
  const tasks   = project?.tasks || []

  return (
    <div className="flex flex-col gap-3">
      {showProject && (
        <Field label={I18n.t("project_planning.fields.project")}>
          <select
            autoFocus={autoFocus}
            className={fieldClass}
            value={fields.projectId}
            onChange={event => set({ projectId: event.target.value, taskId: "" })}
          >
            <option value="">{I18n.t("project_planning.fields.pick_project")}</option>
            {projects.map(item => <option key={item.id} value={item.id}>{projectLabel(item)}</option>)}
          </select>
        </Field>
      )}

      {tasks.length > 0 && (
        <Field label={I18n.t("project_planning.fields.task")}>
          <select className={fieldClass} value={fields.taskId} onChange={event => set({ taskId: event.target.value })}>
            <option value="">{I18n.t("project_planning.fields.no_task")}</option>
            {tasks.map(task => <option key={task.id} value={task.id}>{task.title}</option>)}
          </select>
        </Field>
      )}

      <Field label={I18n.t("project_planning.fields.time")}>
        <div className="flex items-center gap-2">
          <Segmented
            size="sm"
            value={fields.mode}
            onChange={mode => set({ mode })}
            options={[
              { value: "hours", label: I18n.t("project_planning.fields.mode_hours") },
              { value: "times", label: I18n.t("project_planning.fields.mode_times") },
            ]}
          />
          {fields.mode === "hours" ? (
            <div className="relative flex-1">
              <input
                type="number" min="0.25" max="24" step="0.25"
                className={clsx(fieldClass, "pr-7 tabular-nums")}
                placeholder={String(settings.hours_per_day)}
                value={fields.hours}
                onChange={event => set({ hours: event.target.value })}
              />
              <span className="absolute right-2 top-1/2 -translate-y-1/2 text-[11px] text-slate-400 pointer-events-none">h</span>
            </div>
          ) : (
            <div className="flex items-center gap-1 flex-1">
              <input type="time" className={clsx(fieldClass, "tabular-nums")} value={fields.startsAt} onChange={event => set({ startsAt: event.target.value })} />
              <FontAwesomeIcon icon="arrow-right" className="text-[10px] text-slate-400" />
              <input type="time" className={clsx(fieldClass, "tabular-nums")} value={fields.endsAt} onChange={event => set({ endsAt: event.target.value })} />
            </div>
          )}
        </div>
      </Field>

      <Field label={I18n.t("project_planning.fields.note")}>
        <input
          type="text"
          className={fieldClass}
          placeholder={I18n.t("project_planning.fields.note_placeholder")}
          value={fields.note}
          onChange={event => set({ note: event.target.value })}
        />
      </Field>
    </div>
  )
}

export default AssignmentFields
